import { c as _c } from "react/compiler-runtime";
import { useTranslation } from '@/hooks/use-translation';
import type { Key } from '@/lib/piano/keyboard/key';
import type { KeyboardPlacement } from '@/lib/piano/keyboard/keyboard-geometry';
import type { AxisScale } from '../echarts/build-base-option';
import { KeyboardChart } from '../KeyboardChart';
import { ChartKeyboardAxis } from './ChartKeyboardAxis';
export interface ChartEmptyFrameProps {
  /** What to say, laid over the figure: an `EmptyState`. */
  children: React.ReactNode;
  geometry: KeyboardPlacement;
  height: number;
  keys: readonly Key[];
  scale: AxisScale;
  variant: 'analyze' | 'design';
}

/**
 * A figure with nothing to draw yet: its empty axes and keyboard, faded,
 * under a veil that says what is missing.
 *
 * The figure keeps its place and its size, so the page does not jump the
 * moment the first value comes in.
 */
export const ChartEmptyFrame = t0 => {
  const $ = _c(22);
  const {
    children,
    geometry,
    height,
    keys,
    scale,
    variant
  } = t0;
  const {
    t
  } = useTranslation();
  let t1;
  if ($[0] !== t) {
    t1 = t("Chart unavailable");
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== geometry || $[4] !== height || $[5] !== scale || $[6] !== variant) {
    t3 = <KeyboardChart variant={variant} geometry={geometry} height={height} scale={scale} series={t2} />;
    $[3] = geometry;
    $[4] = height;
    $[5] = scale;
    $[6] = variant;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t) {
    t4 = t("Keyboard");
    $[8] = t;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== keys || $[11] !== t4) {
    t5 = <ChartKeyboardAxis keys={keys} marks="none" description={t4} />;
    $[10] = keys;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== t3 || $[14] !== t5) {
    t6 = <div aria-hidden="true" className="px-3 pt-[18px] pb-1.5 opacity-55">{t3}{t5}</div>;
    $[13] = t3;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== children) {
    t7 = <div className="absolute inset-0 flex items-center justify-center bg-ptl-veil p-6">{children}</div>;
    $[16] = children;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== t1 || $[19] !== t6 || $[20] !== t7) {
    t8 = <section aria-label={t1} className="relative overflow-hidden rounded-md border border-ptl-rule-strong bg-card">{t6}{t7}</section>;
    $[18] = t1;
    $[19] = t6;
    $[20] = t7;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  return t8;
};